import React, { useState, useEffect } from 'react';
import {
  BanknotesIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { ModalShell, ModalActions } from './modals/core';

interface Ruecklage {
  id: number;
  name: string;
  typ: 'zweckgebunden' | 'wiederbeschaffung' | 'frei' | 'betriebsmittel';
  zweck?: string;
  ziel_betrag?: number;
  ziel_datum?: string;
  aktueller_stand: number;
}

interface RuecklagenSummary {
  ruecklagen: Ruecklage[];
  total: number;
  jahr: number;
}

interface RuecklagenCardProps {
  isVorstand?: boolean;
  onRefresh?: () => void;
}

const typLabels: Record<string, string> = {
  zweckgebunden: 'Zweckgebunden',
  wiederbeschaffung: 'Wiederbeschaffung',
  frei: 'Freie Rücklage',
  betriebsmittel: 'Betriebsmittel'
};

const typColors: Record<string, string> = {
  zweckgebunden: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300',
  wiederbeschaffung: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
  frei: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300',
  betriebsmittel: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
};

const RuecklagenCard: React.FC<RuecklagenCardProps> = ({ isVorstand = false, onRefresh }) => {
  const [data, setData] = useState<RuecklagenSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedRuecklage, setSelectedRuecklage] = useState<Ruecklage | null>(null);
  const [bookingType, setBookingType] = useState<'zufuehrung' | 'entnahme'>('zufuehrung');

  // Form state für neue Rücklage
  const [formData, setFormData] = useState({
    name: '',
    typ: 'betriebsmittel' as Ruecklage['typ'],
    zweck: '',
    ziel_betrag: '',
    ziel_datum: ''
  });

  // Booking form state
  const [bookingAmount, setBookingAmount] = useState('');
  const [bookingKommentar, setBookingKommentar] = useState('');

  const fetchData = async () => {
    try {
      const response = await apiClient.get('/api/ruecklagen/summary');
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ruecklagen:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddRuecklage = async () => {
    try {
      await apiClient.post('/api/ruecklagen', {
        name: formData.name,
        typ: formData.typ,
        zweck: formData.zweck || null,
        ziel_betrag: formData.ziel_betrag ? parseFloat(formData.ziel_betrag) : null,
        ziel_datum: formData.ziel_datum || null
      });
      setShowAddModal(false);
      setFormData({ name: '', typ: 'betriebsmittel', zweck: '', ziel_betrag: '', ziel_datum: '' });
      fetchData();
      onRefresh?.();
    } catch (error) {
      console.error('Error creating ruecklage:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Rücklage wirklich deaktivieren?')) return;
    try {
      await apiClient.delete(`/api/ruecklagen/${id}`);
      fetchData();
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting ruecklage:', error);
    }
  };

  const openBookingModal = (ruecklage: Ruecklage, type: 'zufuehrung' | 'entnahme') => {
    setSelectedRuecklage(ruecklage);
    setBookingType(type);
    setBookingAmount('');
    setBookingKommentar('');
    setShowBookingModal(true);
  };

  const handleBooking = async () => {
    if (!selectedRuecklage || !bookingAmount) return;

    const amount = parseFloat(bookingAmount);
    if (isNaN(amount) || amount <= 0) return;

    try {
      // Get current year values first
      const currentYear = new Date().getFullYear();
      const detailResponse = await apiClient.get(`/api/ruecklagen/${selectedRuecklage.id}`);
      const jahreswerte = detailResponse.data.data?.jahreswerte || [];
      const currentYearData = jahreswerte.find((j: any) => j.jahr === currentYear);

      const anfangsstand = currentYearData?.anfangsstand || 0;
      const existingZufuehrung = currentYearData?.zufuehrung || 0;
      const existingEntnahme = currentYearData?.entnahme || 0;

      await apiClient.post(`/api/ruecklagen/${selectedRuecklage.id}/jahreswert`, {
        jahr: currentYear,
        anfangsstand: anfangsstand,
        zufuehrung: bookingType === 'zufuehrung' ? existingZufuehrung + amount : existingZufuehrung,
        entnahme: bookingType === 'entnahme' ? existingEntnahme + amount : existingEntnahme,
        kommentar: bookingKommentar || null
      });

      setShowBookingModal(false);
      setSelectedRuecklage(null);
      setBookingAmount('');
      setBookingKommentar('');
      fetchData();
      onRefresh?.();
    } catch (error) {
      console.error('Error booking:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BanknotesIcon className="h-5 w-5 text-amber-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Rücklagen
            </h3>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <InformationCircleIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {isVorstand && (
              <button
                onClick={() => setShowAddModal(true)}
                className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Neue Rücklage anlegen"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              {expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Info Box */}
        {showInfo && (
          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-xs text-amber-800 dark:text-amber-200">
            <strong>Rücklagen (§ 62 AO)</strong> sind reservierte Beträge für bestimmte Zwecke
            wie Wiederbeschaffung, Betriebsmittel oder zweckgebundene Projekte.
            Sie dienen der internen Planung und dem Nachweis gegenüber dem Finanzamt.
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="p-4">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(data?.total || 0)}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {data?.ruecklagen.length || 0} Rücklage{(data?.ruecklagen.length || 0) !== 1 ? 'n' : ''}
          </span>
        </div>
      </div>

      {/* Expanded List */}
      {expanded && data?.ruecklagen && data.ruecklagen.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {data.ruecklagen.map((r) => (
            <div
              key={r.id}
              className="p-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-white">{r.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${typColors[r.typ]}`}>
                      {typLabels[r.typ]}
                    </span>
                  </div>
                  {r.zweck && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{r.zweck}</p>
                  )}
                  {r.ziel_betrag && (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span>Fortschritt</span>
                        <span>{Math.round((r.aktueller_stand / r.ziel_betrag) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-amber-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (r.aktueller_stand / r.ziel_betrag) * 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatCurrency(r.aktueller_stand)} von {formatCurrency(r.ziel_betrag)}
                        {r.ziel_datum && ` bis ${new Date(r.ziel_datum).toLocaleDateString('de-DE')}`}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 ml-4">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(r.aktueller_stand)}
                  </span>
                  {isVorstand && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openBookingModal(r, 'zufuehrung')}
                        className="p-1.5 text-success-600 hover:bg-success-100 dark:hover:bg-success-900/30 rounded-lg transition-colors"
                        title="Zuführung"
                      >
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openBookingModal(r, 'entnahme')}
                        className="p-1.5 text-error-600 hover:bg-error-100 dark:hover:bg-error-900/30 rounded-lg transition-colors"
                        title="Entnahme"
                      >
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-1.5 text-gray-400 hover:text-error-600 hover:bg-error-100 dark:hover:bg-error-900/30 rounded-lg transition-colors"
                        title="Löschen"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {expanded && (!data?.ruecklagen || data.ruecklagen.length === 0) && (
        <div className="p-6 text-center text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
          <BanknotesIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>Keine Rücklagen vorhanden</p>
          {isVorstand && (
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
            >
              Erste Rücklage anlegen
            </button>
          )}
        </div>
      )}

      {/* Add Modal */}
      <ModalShell
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        variant="form"
        size="md"
        title="Neue Rücklage anlegen"
        icon={PlusIcon}
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: () => setShowAddModal(false) }}
            primary={{ label: 'Anlegen', onClick: handleAddRuecklage, disabled: !formData.name }}
          />
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="z.B. Agility-Parcours"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Typ *
            </label>
            <select
              value={formData.typ}
              onChange={(e) => setFormData({ ...formData, typ: e.target.value as Ruecklage['typ'] })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="betriebsmittel">Betriebsmittelrücklage</option>
              <option value="zweckgebunden">Zweckgebunden</option>
              <option value="wiederbeschaffung">Wiederbeschaffung</option>
              <option value="frei">Freie Rücklage</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Zweck/Beschreibung
            </label>
            <textarea
              value={formData.zweck}
              onChange={(e) => setFormData({ ...formData, zweck: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={2}
              placeholder="Wofür wird gespart?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Zielbetrag
              </label>
              <input
                type="number"
                value={formData.ziel_betrag}
                onChange={(e) => setFormData({ ...formData, ziel_betrag: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="0,00"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Zieldatum
              </label>
              <input
                type="date"
                value={formData.ziel_datum}
                onChange={(e) => setFormData({ ...formData, ziel_datum: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>
      </ModalShell>

      {/* Booking Modal */}
      <ModalShell
        open={showBookingModal && !!selectedRuecklage}
        onClose={() => {
          setShowBookingModal(false);
          setSelectedRuecklage(null);
        }}
        variant="form"
        size="md"
        title={bookingType === 'zufuehrung' ? 'Zuführung' : 'Entnahme'}
        subtitle={selectedRuecklage ? `${selectedRuecklage.name} · Aktuell: ${formatCurrency(selectedRuecklage.aktueller_stand)}` : undefined}
        icon={bookingType === 'zufuehrung' ? ArrowUpIcon : ArrowDownIcon}
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{
              label: 'Abbrechen',
              onClick: () => {
                setShowBookingModal(false);
                setSelectedRuecklage(null);
              }
            }}
            primary={{
              label: bookingType === 'zufuehrung' ? 'Zuführen' : 'Entnehmen',
              onClick: handleBooking,
              disabled: !bookingAmount || parseFloat(bookingAmount) <= 0
            }}
            danger={bookingType === 'entnahme'}
          />
        }
      >
        {selectedRuecklage && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Betrag *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={bookingAmount}
                  onChange={(e) => setBookingAmount(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0,00"
                  step="0.01"
                  min="0.01"
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kommentar (optional)
              </label>
              <input
                type="text"
                value={bookingKommentar}
                onChange={(e) => setBookingKommentar(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="z.B. Aus Überschuss 2025"
              />
            </div>

            {bookingAmount && parseFloat(bookingAmount) > 0 && (
              <div className={`p-3 rounded-lg ${bookingType === 'zufuehrung' ? 'bg-success-50 dark:bg-success-900/20' : 'bg-error-50 dark:bg-error-900/20'}`}>
                <p className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Neuer Stand: </span>
                  <span className={`font-semibold ${bookingType === 'zufuehrung' ? 'text-success-700 dark:text-success-300' : 'text-error-700 dark:text-error-300'}`}>
                    {formatCurrency(
                      bookingType === 'zufuehrung'
                        ? selectedRuecklage.aktueller_stand + parseFloat(bookingAmount)
                        : selectedRuecklage.aktueller_stand - parseFloat(bookingAmount)
                    )}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}
      </ModalShell>
    </div>
  );
};

export default RuecklagenCard;
